import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  Legend,
} from 'recharts';
import { useDataStore, useAuthStore } from '@/lib/store';
import { api } from '@/lib/api';
import LoadingSpinner from '@/components/LoadingSpinner';
import type {
  Trade,
  Suggestion,
  AggregatedChartDataPoint,
  PriceHistoryPoint,
  TokenMappingsData,
  ChartInterval,
  ChartRange,
  ChartDataMeta,
  Balance,
} from '@/types';

// Fallback color palette for tokens not in mappings
const FALLBACK_COLORS = [
  '#0ea5e9', // sky-500
  '#8b5cf6', // violet-500
  '#f59e0b', // amber-500
  '#10b981', // emerald-500
  '#ec4899', // pink-500
  '#6366f1', // indigo-500
  '#f97316', // orange-500
  '#14b8a6', // teal-500
  '#ef4444', // red-500
  '#84cc16', // lime-500
];

export default function DashboardPage() {
  const { user } = useAuthStore();
  const { portfolio, configs, isLoadingPortfolio, fetchPortfolio, fetchConfigs } = useDataStore();
  const [recentTrades, setRecentTrades] = useState<Trade[]>([]);
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null);
  const [isLoadingSuggestion, setIsLoadingSuggestion] = useState(false);
  const [withdrawingChain, setWithdrawingChain] = useState<string | null>(null);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);
  const [withdrawSuccess, setWithdrawSuccess] = useState<string | null>(null);

  // Chart state
  const [chartData, setChartData] = useState<AggregatedChartDataPoint[]>([]);
  const [priceHistory, setPriceHistory] = useState<PriceHistoryPoint[]>([]);
  const [tokenMappings, setTokenMappings] = useState<TokenMappingsData | null>(null);
  const [chartMeta, setChartMeta] = useState<ChartDataMeta | null>(null);
  const [isLoadingChart, setIsLoadingChart] = useState(false);
  const [chartInterval, setChartInterval] = useState<ChartInterval>('1d');
  const [chartRange, setChartRange] = useState<ChartRange>('month');

  useEffect(() => {
    fetchPortfolio();
    fetchConfigs();
    loadRecentTrades();
  }, []);

  // Fetch chart data when interval or range changes
  useEffect(() => {
    loadChartData();
  }, [chartInterval, chartRange]);

  const loadChartData = async () => {
    setIsLoadingChart(true);
    try {
      const chartResponse = await api.getChartData(chartInterval, chartRange);

      let chartData = chartResponse.chartData;
      let priceHistory = chartResponse.priceHistory || [];

      // Extend chart to today if last data point is in the past
      if (chartData.length > 0 && priceHistory.length > 0) {
        const lastChartPoint = chartData[chartData.length - 1];
        const lastTimestamp = new Date(lastChartPoint.timestamp);
        const now = new Date();

        // Check if we need to add a point for today (more than 1 hour gap for short ranges, 1 day for longer)
        const hoursDiff = (now.getTime() - lastTimestamp.getTime()) / (1000 * 60 * 60);
        const needsExtension =
          (chartInterval === '1m' && hoursDiff > 1) ||
          (chartInterval === '1h' && hoursDiff > 2) ||
          (chartInterval === '1d' && hoursDiff > 24);

        if (needsExtension) {
          // Add a data point for now with the same token balances
          const nowTimestamp = now.toISOString();
          chartData = [...chartData, {
            timestamp: nowTimestamp,
            totalValue: lastChartPoint.totalValue,
            tokens: lastChartPoint.tokens
          }];

          // Extend price history to now for each token (keeping last known prices)
          const lastPricesByToken = new Map<string, number>();
          priceHistory.forEach(p => {
            lastPricesByToken.set(p.symbol, p.price);
          });

          lastPricesByToken.forEach((price, symbol) => {
            priceHistory.push({
              timestamp: nowTimestamp,
              symbol,
              price
            });
          });
        }
      }

      setChartData(chartData);
      setPriceHistory(priceHistory);
      setTokenMappings(chartResponse.tokenMappings || null);
      setChartMeta(chartResponse.meta);

      // Auto-adjust if invalid combination
      if (!chartResponse.meta.validCombination && chartResponse.meta.suggestion) {
        setChartInterval(chartResponse.meta.suggestion.interval);
        setChartRange(chartResponse.meta.suggestion.range);
      }
    } catch (error) {
      console.error('Failed to load chart data:', error);
    } finally {
      setIsLoadingChart(false);
    }
  };

  const loadRecentTrades = async () => {
    try {
      const { trades } = await api.getTrades(5);
      setRecentTrades(trades);
    } catch (error) {
      console.error('Failed to load trades:', error);
    }
  };

  const loadSuggestion = async () => {
    setIsLoadingSuggestion(true);
    try {
      const { suggestion } = await api.getSuggestion();
      setSuggestion(suggestion);
    } catch (error) {
      console.error('Failed to load suggestion:', error);
    } finally {
      setIsLoadingSuggestion(false);
    }
  };

  const handleWithdraw = async (chainName: string, chainId: number) => {
    setWithdrawingChain(chainName);
    setWithdrawError(null);
    setWithdrawSuccess(null);
    try {
      const response = await api.initiateWithdraw(chainId);
      if (response.success) {
        setWithdrawSuccess(`Withdrawal initiated for ${chainName}. Check your wallet for incoming transactions.`);
        // Refresh portfolio after a delay
        setTimeout(() => {
          fetchPortfolio();
        }, 3000);
      } else {
        setWithdrawError(response.error || 'Withdrawal failed');
      }
    } catch (error) {
      console.error('Failed to initiate withdrawal:', error);
      setWithdrawError(error instanceof Error ? error.message : 'Failed to initiate withdrawal');
    } finally {
      setWithdrawingChain(null);
    }
  };

  // Group balances by chain
  const balancesByChain = useMemo(() => {
    if (!portfolio?.balances) return [];

    const chainMap = new Map<number, { chainId: number; chainName: string; balances: Balance[]; totalValue: number }>();

    portfolio.balances.forEach((balance) => {
      const existing = chainMap.get(balance.chainId);
      if (existing) {
        existing.balances.push(balance);
        existing.totalValue += balance.usdValue;
      } else {
        chainMap.set(balance.chainId, {
          chainId: balance.chainId,
          chainName: balance.chainName,
          balances: [balance],
          totalValue: balance.usdValue,
        });
      }
    });

    return Array.from(chainMap.values()).sort((a, b) => b.totalValue - a.totalValue);
  }, [portfolio?.balances]);

  // Check if user has any balance
  const hasBalance = useMemo(() => {
    return (portfolio?.totalValue || 0) > 0;
  }, [portfolio?.totalValue]);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);

  const formatPercent = (value: number) =>
    `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;

  // Get color for a token from tokenMappings or fallback
  const getTokenColor = useCallback((symbol: string, index: number): string => {
    if (tokenMappings?.chartColors) {
      const color = tokenMappings.chartColors[symbol];
      if (color) return color;
      // Try DEFAULT color
      if (tokenMappings.chartColors.DEFAULT) {
        return tokenMappings.chartColors.DEFAULT;
      }
    }
    return FALLBACK_COLORS[index % FALLBACK_COLORS.length];
  }, [tokenMappings]);

  // Get all unique tokens from chart data (normalized, deduplicated)
  const uniqueTokens = useMemo(() => {
    const tokens = new Set<string>();
    chartData.forEach((point) => {
      point.tokens.forEach((t) => tokens.add(t.symbol));
    });
    return Array.from(tokens);
  }, [chartData]);

  // Get unique tokens from price history - only tokens user has/had in selected period
  const priceTokens = useMemo(() => {
    const tokens = new Set<string>();
    priceHistory.forEach((p) => {
      // Skip stablecoins - their price is always 1
      // Only include tokens that user has/had (appear in chartData)
      if (!tokenMappings?.stablecoins?.includes(p.symbol) && uniqueTokens.includes(p.symbol)) {
        tokens.add(p.symbol);
      }
    });
    return Array.from(tokens);
  }, [priceHistory, tokenMappings, uniqueTokens]);

  // Calculate which token has the highest USD value (for default visibility)
  const highestValueToken = useMemo(() => {
    const tokenTotals = new Map<string, number>();
    chartData.forEach((point) => {
      point.tokens.forEach((t) => {
        tokenTotals.set(t.symbol, (tokenTotals.get(t.symbol) || 0) + t.value);
      });
    });
    let maxToken = '';
    let maxValue = 0;
    tokenTotals.forEach((value, symbol) => {
      // Only consider non-stablecoin tokens that have price data
      if (value > maxValue && priceTokens.includes(symbol)) {
        maxValue = value;
        maxToken = symbol;
      }
    });
    return maxToken;
  }, [chartData, priceTokens]);

  // State for visible price tokens (checkboxes)
  const [visiblePriceTokens, setVisiblePriceTokens] = useState<Set<string>>(new Set());

  // Update visible tokens when highest value token changes
  useEffect(() => {
    if (highestValueToken) {
      setVisiblePriceTokens(new Set([highestValueToken]));
    }
  }, [highestValueToken]);

  // Toggle visibility of a price token
  const togglePriceToken = (symbol: string) => {
    setVisiblePriceTokens((prev) => {
      const next = new Set(prev);
      if (next.has(symbol)) {
        next.delete(symbol);
      } else {
        next.add(symbol);
      }
      return next;
    });
  };

  // Calculate normalized prices (percentage change from first value) for volatility display
  // Also store actual prices for tooltip display
  const normalizedPriceData = useMemo(() => {
    const firstPrices = new Map<string, number>();
    // Find first price for each token
    priceHistory.forEach((p) => {
      if (!firstPrices.has(p.symbol)) {
        firstPrices.set(p.symbol, p.price);
      }
    });
    // Normalize to percentage change and store actual prices
    const normalized: Record<string, Record<string, number>> = {};
    priceHistory.forEach((p) => {
      if (!normalized[p.timestamp]) {
        normalized[p.timestamp] = {};
      }
      const firstPrice = firstPrices.get(p.symbol) || p.price;
      // Normalize: 0 = first price, positive = % gain, negative = % loss
      const percentChange = firstPrice !== 0 ? ((p.price - firstPrice) / firstPrice) * 100 : 0;
      normalized[p.timestamp][`price_${p.symbol}`] = percentChange;
      // Store actual price for tooltip
      normalized[p.timestamp][`actualPrice_${p.symbol}`] = p.price;
    });
    return normalized;
  }, [priceHistory]);

  // Transform chart data for combined bar + line chart
  const transformedChartData = useMemo(() => {
    return chartData.map((point) => {
      const row: Record<string, unknown> = {
        timestamp: point.timestamp,
        totalValue: point.totalValue,
      };
      // Add each token's USD value as a property for bars
      point.tokens.forEach((t) => {
        row[t.symbol] = t.value;
      });
      // Add normalized price data for lines (percentage change from first value)
      const normalizedPrices = normalizedPriceData[point.timestamp];
      if (normalizedPrices) {
        Object.assign(row, normalizedPrices);
      }
      return row;
    });
  }, [chartData, normalizedPriceData]);

  // Format timestamp based on interval
  const formatTimestamp = (value: string) => {
    const date = new Date(value);
    switch (chartInterval) {
      case '1m':
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      case '1h':
        return date.toLocaleString([], {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
        });
      case '1d':
      default:
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  // Interval display names
  const intervalOptions: { value: ChartInterval; label: string }[] = [
    { value: '1m', label: '1 min' },
    { value: '1h', label: '1 hour' },
    { value: '1d', label: '1 day' },
  ];

  // Range display names
  const rangeOptions: { value: ChartRange; label: string }[] = [
    { value: 'day', label: '1D' },
    { value: 'week', label: '1W' },
    { value: 'month', label: '1M' },
    { value: 'year', label: '1Y' },
    { value: 'all', label: 'ALL' },
  ];

  // Check if a range option should be disabled for current interval
  const isRangeDisabled = (range: ChartRange): boolean => {
    // 1-day interval for 1-day range gives only 1 point
    if (chartInterval === '1d' && range === 'day') return true;
    // 1-minute interval doesn't make sense for year/all
    if (chartInterval === '1m' && (range === 'year' || range === 'all')) return true;
    return false;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-dark-400">
          Welcome back{user?.telegramUsername ? `, @${user.telegramUsername}` : ''}
        </p>
      </div>

      {/* Portfolio Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <p className="text-sm text-dark-400 mb-1">Total Portfolio Value</p>
          {isLoadingPortfolio ? (
            <LoadingSpinner size="sm" />
          ) : (
            <p className="text-2xl font-bold text-white">
              {formatCurrency(portfolio?.totalValue || 0)}
            </p>
          )}
        </div>

        <div className="card">
          <p className="text-sm text-dark-400 mb-1">Total P&L</p>
          {isLoadingPortfolio ? (
            <LoadingSpinner size="sm" />
          ) : (
            <div className="flex items-baseline space-x-2">
              <p
                className={`text-2xl font-bold ${
                  (portfolio?.totalPnL || 0) >= 0 ? 'text-green-500' : 'text-red-500'
                }`}
              >
                {formatCurrency(portfolio?.totalPnL || 0)}
              </p>
              <span
                className={`text-sm ${
                  (portfolio?.totalPnLPercent || 0) >= 0 ? 'text-green-500' : 'text-red-500'
                }`}
              >
                {formatPercent(portfolio?.totalPnLPercent || 0)}
              </span>
            </div>
          )}
        </div>

        <div className="card">
          <p className="text-sm text-dark-400 mb-1">Active Strategies</p>
          <p className="text-2xl font-bold text-white">
            {configs.filter((c) => c.isActive).length}
            <span className="text-dark-400 text-base font-normal ml-1">
              / {configs.length}
            </span>
          </p>
        </div>
      </div>

      {/* Portfolio Chart */}
      <div className="card">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
          <h2 className="text-lg font-semibold text-white">Portfolio Value</h2>
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Interval selector */}
            <div className="flex items-center space-x-2">
              <span className="text-xs text-dark-400">Interval:</span>
              <div className="flex space-x-1">
                {intervalOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setChartInterval(opt.value)}
                    className={`px-2 py-1 text-xs rounded-md transition-colors ${
                      chartInterval === opt.value
                        ? 'bg-primary-600 text-white'
                        : 'bg-dark-700 text-dark-300 hover:bg-dark-600 hover:text-white'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            {/* Range selector */}
            <div className="flex items-center space-x-2">
              <span className="text-xs text-dark-400">Range:</span>
              <div className="flex space-x-1">
                {rangeOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => !isRangeDisabled(opt.value) && setChartRange(opt.value)}
                    disabled={isRangeDisabled(opt.value)}
                    className={`px-2 py-1 text-xs rounded-md transition-colors ${
                      chartRange === opt.value
                        ? 'bg-primary-600 text-white'
                        : isRangeDisabled(opt.value)
                          ? 'bg-dark-800 text-dark-500 cursor-not-allowed'
                          : 'bg-dark-700 text-dark-300 hover:bg-dark-600 hover:text-white'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Token price volatility checkboxes */}
        {priceTokens.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 mb-4 pb-3 border-b border-dark-700">
            <span className="text-xs text-dark-400">Show volatility:</span>
            {priceTokens.map((symbol, index) => (
              <label
                key={symbol}
                className="flex items-center gap-1.5 cursor-pointer select-none"
              >
                <input
                  type="checkbox"
                  checked={visiblePriceTokens.has(symbol)}
                  onChange={() => togglePriceToken(symbol)}
                  className="w-3.5 h-3.5 rounded border-dark-500 bg-dark-700 text-primary-500 focus:ring-primary-500 focus:ring-offset-0"
                />
                <span
                  className="text-xs font-medium"
                  style={{ color: getTokenColor(symbol, index) }}
                >
                  {symbol}
                </span>
              </label>
            ))}
          </div>
        )}

        <div className="h-80">
          {isLoadingChart ? (
            <div className="h-full flex items-center justify-center">
              <LoadingSpinner size="lg" />
            </div>
          ) : transformedChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={transformedChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis
                  dataKey="timestamp"
                  stroke="#64748b"
                  tick={{ fill: '#64748b', fontSize: 11 }}
                  tickFormatter={formatTimestamp}
                  interval="preserveStartEnd"
                />
                <YAxis
                  yAxisId="left"
                  stroke="#64748b"
                  tick={{ fill: '#64748b', fontSize: 11 }}
                  tickFormatter={(value) => `$${value.toLocaleString()}`}
                />
                {/* Hidden Y-axis for normalized price volatility lines */}
                <YAxis
                  yAxisId="volatility"
                  orientation="right"
                  hide={true}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e293b',
                    border: '1px solid #334155',
                    borderRadius: '8px',
                  }}
                  labelStyle={{ color: '#94a3b8' }}
                  formatter={(value: number, name: string, props: { payload?: Record<string, number> }) => {
                    // Hide shadow lines from tooltip
                    if (name.startsWith('shadow_')) {
                      return [null, null];
                    }
                    // Format price lines - show actual price from payload
                    if (name.startsWith('price_')) {
                      const tokenName = name.replace('price_', '');
                      const actualPrice = props.payload?.[`actualPrice_${tokenName}`];
                      return [formatCurrency(actualPrice ?? 0), `${tokenName} Price`];
                    }
                    return [formatCurrency(value), name];
                  }}
                  labelFormatter={(label) => {
                    const date = new Date(label);
                    return chartInterval === '1m'
                      ? date.toLocaleString()
                      : chartInterval === '1h'
                        ? date.toLocaleString([], {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : date.toLocaleDateString([], {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          });
                  }}
                />
                <Legend
                  wrapperStyle={{ paddingTop: '10px' }}
                  formatter={(value) => {
                    // Clean up price line names for legend
                    const displayName = value.startsWith('price_')
                      ? `${value.replace('price_', '')} Price`
                      : value;
                    return <span className="text-xs text-dark-300">{displayName}</span>;
                  }}
                  // Filter out duplicate entries - show each token once, only show visible price tokens
                  payload={[
                    ...uniqueTokens.map((symbol, index) => ({
                      value: symbol,
                      type: 'square' as const,
                      color: getTokenColor(symbol, index),
                    })),
                    ...priceTokens
                      .filter((symbol) => visiblePriceTokens.has(symbol))
                      .map((symbol, index) => ({
                        value: `price_${symbol}`,
                        type: 'line' as const,
                        color: getTokenColor(symbol, index),
                      })),
                  ]}
                />
                {/* Token value bars (stacked) */}
                {uniqueTokens.map((symbol, index) => (
                  <Bar
                    key={symbol}
                    dataKey={symbol}
                    stackId="tokens"
                    yAxisId="left"
                    fill={getTokenColor(symbol, index)}
                    name={symbol}
                  />
                ))}
                {/* Token volatility lines - shadow/glow effect (background line) */}
                {priceTokens
                  .filter((symbol) => visiblePriceTokens.has(symbol))
                  .map((symbol) => (
                    <Line
                      key={`shadow_${symbol}`}
                      type="monotone"
                      dataKey={`price_${symbol}`}
                      yAxisId="volatility"
                      stroke="#0f172a"
                      strokeWidth={6}
                      dot={false}
                      name={`shadow_${symbol}`}
                      connectNulls
                      legendType="none"
                    />
                  ))}
                {/* Token volatility lines - main colored line */}
                {priceTokens
                  .filter((symbol) => visiblePriceTokens.has(symbol))
                  .map((symbol, index) => (
                    <Line
                      key={`price_${symbol}`}
                      type="monotone"
                      dataKey={`price_${symbol}`}
                      yAxisId="volatility"
                      stroke={getTokenColor(symbol, index)}
                      strokeWidth={2.5}
                      dot={false}
                      name={`price_${symbol}`}
                      connectNulls
                    />
                  ))}
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-dark-400">
              No chart data available yet
            </div>
          )}
        </div>
        {chartMeta && (
          <div className="mt-2 text-xs text-dark-400 text-right">
            {chartMeta.totalPoints} data points
            {chartMeta.startDate && (
              <> • From {new Date(chartMeta.startDate).toLocaleDateString()}</>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Trades */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Recent Trades</h2>
          {recentTrades.length > 0 ? (
            <div className="space-y-3">
              {recentTrades.map((trade) => (
                <div
                  key={trade.tradeId}
                  className="flex items-center justify-between py-2 border-b border-dark-700 last:border-0"
                >
                  <div className="flex items-center space-x-3">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded ${
                        trade.action === 'BUY'
                          ? 'bg-green-900/50 text-green-400'
                          : 'bg-red-900/50 text-red-400'
                      }`}
                    >
                      {trade.action}
                    </span>
                    <div>
                      <p className="text-white font-medium">
                        {trade.baseSymbol}/{trade.quoteSymbol}
                      </p>
                      <p className="text-xs text-dark-400">{trade.chainName}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-white">{formatCurrency(trade.price)}</p>
                    <p className="text-xs text-dark-400">
                      {new Date(trade.createdAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-dark-400 text-center py-4">No trades yet</p>
          )}
        </div>

        {/* AI Suggestion */}
        <div className="card">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4">
            <h2 className="text-lg font-semibold text-white">AI Suggestion</h2>
            <button
              onClick={loadSuggestion}
              disabled={isLoadingSuggestion}
              className="btn btn-secondary text-sm"
            >
              {isLoadingSuggestion ? (
                <LoadingSpinner size="sm" />
              ) : (
                'Get Suggestion'
              )}
            </button>
          </div>

          {/* Show deposit message if no balance */}
          {!isLoadingPortfolio && !hasBalance && (
            <div className="bg-amber-900/30 border border-amber-700/50 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <div className="text-amber-500 mt-0.5">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-amber-200 font-medium">No Balance Detected</p>
                  <p className="text-amber-300/80 text-sm mt-1">
                    To get meaningful trading suggestions, please deposit funds to your Smart Contract Wallet first.
                    Check Telegram for your deposit address.
                  </p>
                </div>
              </div>
            </div>
          )}

          {suggestion ? (
            <div className="bg-dark-700/50 rounded-lg p-4">
              <p className="text-dark-200 leading-relaxed whitespace-pre-wrap">{suggestion.suggestion}</p>
              <p className="text-xs text-dark-400 mt-3">
                Generated on {new Date(suggestion.createdAt).toLocaleString()}
              </p>
            </div>
          ) : (
            <p className="text-dark-400 text-center py-8">
              {hasBalance
                ? 'Click "Get Suggestion" to receive personalized trading advice based on your portfolio and trading history.'
                : 'Deposit funds first, then click "Get Suggestion" for personalized trading advice.'
              }
            </p>
          )}
        </div>
      </div>

      {/* Token Balances with Withdraw */}
      {balancesByChain.length > 0 && (
        <div className="card">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
            <h2 className="text-lg font-semibold text-white">Token Balances</h2>
            {(withdrawSuccess || withdrawError) && (
              <div className={`text-sm px-3 py-1 rounded-lg ${
                withdrawSuccess ? 'bg-green-900/50 text-green-400' : 'bg-red-900/50 text-red-400'
              }`}>
                {withdrawSuccess || withdrawError}
              </div>
            )}
          </div>
          <div className="space-y-4">
            {balancesByChain.map((chainGroup) => (
              <div key={chainGroup.chainId} className="bg-dark-700/30 rounded-lg p-4">
                {/* Chain Header with Withdraw Button */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-3 pb-3 border-b border-dark-600">
                  <div className="flex items-center gap-2">
                    <span className="text-primary-400 font-medium">{chainGroup.chainName}</span>
                    <span className="text-dark-400 text-sm">
                      ({chainGroup.balances.length} token{chainGroup.balances.length !== 1 ? 's' : ''})
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-white font-semibold">
                      {formatCurrency(chainGroup.totalValue)}
                    </span>
                    {chainGroup.totalValue > 0 && (
                      <button
                        onClick={() => handleWithdraw(chainGroup.chainName, chainGroup.chainId)}
                        disabled={withdrawingChain === chainGroup.chainName}
                        className="btn btn-secondary text-xs px-3 py-1.5 flex items-center gap-1.5"
                      >
                        {withdrawingChain === chainGroup.chainName ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <>
                            <WithdrawIcon className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">Withdraw All</span>
                            <span className="sm:hidden">Withdraw</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
                {/* Token List */}
                <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
                  <table className="w-full min-w-[280px]">
                    <thead>
                      <tr className="text-left text-dark-400 text-xs">
                        <th className="pb-2 font-medium">Token</th>
                        <th className="pb-2 font-medium text-right">Balance</th>
                        <th className="pb-2 font-medium text-right">USD Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {chainGroup.balances.map((balance, idx) => (
                        <tr
                          key={idx}
                          className="border-t border-dark-600/50"
                        >
                          <td className="py-2">
                            <span className="font-medium text-white text-sm">
                              {balance.symbol}
                            </span>
                          </td>
                          <td className="py-2 text-right text-dark-200 font-mono text-sm">
                            {balance.balance.toLocaleString(undefined, {
                              maximumFractionDigits: 6,
                            })}
                          </td>
                          <td className="py-2 text-right text-white text-sm">
                            {formatCurrency(balance.usdValue)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty Balance State */}
      {!isLoadingPortfolio && balancesByChain.length === 0 && (
        <div className="card text-center py-8">
          <div className="text-dark-400 mb-4">
            <WalletIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="text-lg font-medium text-white mb-2">No Balance Found</p>
            <p>Deposit funds to your Smart Contract Wallet to start trading.</p>
          </div>
        </div>
      )}
    </div>
  );
}

function WithdrawIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
    </svg>
  );
}

function WalletIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  );
}
